/**
 * The flight Form core: the Drafts behind the flight modals, their entry
 * states, transitions, validation and payload builders. Pure functions only —
 * the modals render and wire, this module owns the logic.
 *
 * Manual legs carry Wall-clock times, so nothing here ever computes a duration
 * from them (there is no zone to subtract); builders only convert between
 * input strings and wire format.
 */

import {
	FlightCreateSource,
	FlightResponse,
	GetFlightGroupResponse,
	GetUserType,
	PatchFlightGroupType,
	PatchFlightType,
	PostFlightAutoType,
	PostFlightManualType,
} from '@orbit/api-types'
import { inputToWallClock, toDashed, wallClockDay, wallClockToInput } from '@lib/datetime'
import { compactLinks } from '@lib/links'
import { splitFlightCode } from './flightHelpers'

export type FlightMode = 'auto' | 'manual'

/** The entry-mode switch's options, in display order. */
export const MODE_OPTIONS: ReadonlyArray<{ value: FlightMode; label: string }> = [
	{ value: 'auto', label: 'Auto Lookup' },
	{ value: 'manual', label: 'Manual Entry' },
]

/** The manual-entry leg fields, shared by Add (manual mode) and Edit Leg. */
export interface ManualLegDraft {
	airlineIata: string
	flightNumber: string
	departureAirport: string
	arrivalAirport: string
	/** `datetime-local` input values carrying Wall-clock times. */
	departureTime: string
	arrivalTime: string
}

/** The Add Flight Draft: one lookup form plus one manual form, sharing passengers. */
export interface FlightDraft {
	mode: FlightMode
	flightCode: string
	flightDate: string
	leg: ManualLegDraft
	passengerIds: Set<number>
	/** Set when an auto lookup came back empty and seeded the manual form. */
	manualFallbackActive: boolean
}

const emptyLeg = (): ManualLegDraft => ({
	airlineIata: '',
	flightNumber: '',
	departureAirport: '',
	arrivalAirport: '',
	departureTime: '',
	arrivalTime: '',
})

/**
 * Add's entry state: auto lookup, with passengers preset from the group being
 * extended, or from every trip member when starting a new flight.
 */
export const emptyFlightDraft = (
	members: GetUserType[],
	initialFlightGroup: GetFlightGroupResponse | undefined,
): FlightDraft => ({
	mode: 'auto',
	flightCode: '',
	flightDate: '',
	leg: emptyLeg(),
	passengerIds: new Set(
		initialFlightGroup
			? initialFlightGroup.flights.flatMap(leg => leg.passengers.map(p => p.userId))
			: members.map(m => m.id),
	),
	manualFallbackActive: false,
})

/** The Edit Leg Draft. Auto-imported legs only ever edit their passengers. */
export interface LegEditDraft {
	leg: ManualLegDraft
	passengerIds: Set<number>
}

/** Edit's entry state, hydrated from the stored leg (linked-airport fallback included). */
export const legDraftFromFlight = (leg: FlightResponse): LegEditDraft => ({
	leg: {
		airlineIata: leg.airlineIata ?? '',
		flightNumber: leg.flightNumber ?? '',
		departureAirport: leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? '',
		arrivalAirport: leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? '',
		departureTime: wallClockToInput(leg.departureTimeStr),
		arrivalTime: wallClockToInput(leg.arrivalTimeStr),
	},
	passengerIds: new Set(leg.passengers.map(p => p.userId)),
})

// ── transitions: Draft → Draft, never mutating in place ──

/** Both Drafts carry the same passenger set, so one toggle serves both. */
export const togglePassenger = <T extends { passengerIds: Set<number> }>(draft: T, userId: number): T => {
	const passengerIds = new Set(draft.passengerIds)
	if (passengerIds.has(userId)) {
		passengerIds.delete(userId)
	} else {
		passengerIds.add(userId)
	}
	return { ...draft, passengerIds }
}

/** Sets one text field of whichever Draft holds the leg; the times have their own transitions below. */
export const setLegField = <T extends { leg: ManualLegDraft }>(
	draft: T,
	field: keyof ManualLegDraft,
	value: string,
): T => ({ ...draft, leg: { ...draft.leg, [field]: value } })

/**
 * Sets the departure on whichever Draft holds the leg; an arrival that is no
 * longer after it is cleared rather than left inverted.
 */
export const setLegDepartureTime = <T extends { leg: ManualLegDraft }>(draft: T, value: string): T => ({
	...draft,
	leg: {
		...draft.leg,
		departureTime: value,
		arrivalTime: draft.leg.arrivalTime && value && value > draft.leg.arrivalTime ? '' : draft.leg.arrivalTime,
	},
})

/** The mirror of `setLegDepartureTime`: a departure no longer before the arrival is cleared. */
export const setLegArrivalTime = <T extends { leg: ManualLegDraft }>(draft: T, value: string): T => ({
	...draft,
	leg: {
		...draft.leg,
		arrivalTime: value,
		departureTime:
			draft.leg.departureTime && value && value < draft.leg.departureTime ? '' : draft.leg.departureTime,
	},
})

/**
 * Auto lookup came back empty: seed the manual form from what the user typed —
 * the split code, and noon on the chosen date as a starting departure — and
 * switch over so they can finish by hand.
 */
export const applyAutoLookupFailure = (draft: FlightDraft): FlightDraft => {
	const { airlineIata, flightNumber } = splitFlightCode(draft.flightCode)
	return {
		...draft,
		mode: 'manual',
		manualFallbackActive: true,
		leg: {
			...draft.leg,
			airlineIata,
			flightNumber,
			departureTime: draft.flightDate ? `${draft.flightDate}T12:00` : draft.leg.departureTime,
		},
	}
}

/** A leg was saved: reset only the active form so the next leg starts clean. */
export const afterLegAdded = (draft: FlightDraft): FlightDraft =>
	draft.mode === 'auto'
		? { ...draft, flightCode: '', flightDate: '', manualFallbackActive: false }
		: { ...draft, leg: emptyLeg(), manualFallbackActive: false }

/** "Add Another Leg": back to auto lookup, fallback notice dismissed. */
export const startAnotherLeg = (draft: FlightDraft): FlightDraft => ({
	...draft,
	mode: 'auto',
	manualFallbackActive: false,
})

// ── picker defaults: where an empty date picker opens, never a filled value ──

/** The `YYYY-MM-DD` day each empty picker opens on; `''` leaves it on today. */
export interface PickerOpenDates {
	departure: string
	arrival: string
}

/**
 * A later leg departs where the previous one ends: the latest arrival in the
 * group (its departure when it has none). The first leg opens on the trip start.
 * The arrival opens on the chosen departure, else wherever the departure would.
 * Wall-clock strings compare lexicographically, so no zone is ever involved.
 */
export const pickerOpenDates = (
	draft: FlightDraft,
	legs: FlightResponse[],
	tripStartDate: string | null | undefined,
): PickerOpenDates => {
	const previousLegEnd = legs
		.map(leg => leg.arrivalTimeStr || leg.departureTimeStr)
		.filter(Boolean)
		.sort()
		.at(-1)
	const departure = previousLegEnd ? wallClockDay(previousLegEnd) : toDashed(tripStartDate)
	const arrival = wallClockDay(draft.leg.departureTime) || departure
	return { departure, arrival }
}

// ── validation ──

export interface FlightValidation {
	valid: boolean
	/** The message shown under the passenger list, or null when it is satisfied. */
	passengerError: string | null
}

const passengerError = (passengerIds: Set<number>): string | null =>
	passengerIds.size === 0 ? 'Select at least one passenger' : null

/** Mode-aware: auto needs a code and a date, manual an airline and a departure. Both need passengers. */
export const validateFlightDraft = (draft: FlightDraft): FlightValidation => {
	const error = passengerError(draft.passengerIds)
	const fieldsValid =
		draft.mode === 'auto'
			? draft.flightCode.trim().length > 0 && draft.flightDate.length > 0
			: draft.leg.airlineIata.trim().length > 0 && draft.leg.departureTime.length > 0
	return { valid: fieldsValid && error === null, passengerError: error }
}

/** An auto-imported leg edits passengers only, so its flight fields are not required. */
export const validateLegEditDraft = (draft: LegEditDraft, isManual: boolean): FlightValidation => {
	const error = passengerError(draft.passengerIds)
	const fieldsValid =
		!isManual || (draft.leg.airlineIata.trim().length > 0 && draft.leg.departureTime.length > 0)
	return { valid: fieldsValid && error === null, passengerError: error }
}

// ── builders: where a Draft crosses into wire format ──

/**
 * The auto-lookup create payload; the server resolves the code into real instants.
 * `source` is where a new flight was started, which the server records it with.
 */
export const toAutoCreatePayload = (
	draft: FlightDraft,
	flightGroupId: number | undefined,
	source?: FlightCreateSource,
): PostFlightAutoType => ({
	flightCode: draft.flightCode.trim(),
	date: draft.flightDate,
	passengerUserIds: Array.from(draft.passengerIds),
	flightGroupId,
	...(source && { source }),
})

/**
 * One wall-clock leg. Blank optionals are OMITTED on create, and no duration is
 * ever sent — manual times float, so there is nothing to subtract them against.
 */
export const toManualCreatePayload = (
	draft: FlightDraft,
	flightGroupId: number | undefined,
	source?: FlightCreateSource,
): PostFlightManualType => ({
	flights: [
		{
			airlineIata: draft.leg.airlineIata.trim().toUpperCase(),
			flightNumber: draft.leg.flightNumber.trim() || undefined,
			departureAirportCode: draft.leg.departureAirport.trim().toUpperCase() || undefined,
			arrivalAirportCode: draft.leg.arrivalAirport.trim().toUpperCase() || undefined,
			departureTimeStr: inputToWallClock(draft.leg.departureTime),
			arrivalTimeStr: draft.leg.arrivalTime ? inputToWallClock(draft.leg.arrivalTime) : undefined,
		},
	],
	passengerUserIds: Array.from(draft.passengerIds),
	flightGroupId,
	...(source && { source }),
})

/**
 * PATCH semantics: a manual leg sends every field, CLEARING the optionals the
 * user emptied with an explicit null; an auto-imported leg patches only its
 * passengers, leaving the looked-up details untouched.
 */
export const toLegUpdatePayload = (draft: LegEditDraft, isManual: boolean): PatchFlightType =>
	isManual
		? {
				airlineIata: draft.leg.airlineIata.trim().toUpperCase(),
				flightNumber: draft.leg.flightNumber.trim() || null,
				departureAirportCode: draft.leg.departureAirport.trim().toUpperCase() || null,
				arrivalAirportCode: draft.leg.arrivalAirport.trim().toUpperCase() || null,
				departureTimeStr: inputToWallClock(draft.leg.departureTime),
				arrivalTimeStr: draft.leg.arrivalTime ? inputToWallClock(draft.leg.arrivalTime) : null,
				passengerUserIds: Array.from(draft.passengerIds),
			}
		: {
				passengerUserIds: Array.from(draft.passengerIds),
			}

// ── edit flight group: the notes and links carried by the group, not a leg ──

export interface FlightGroupDraft {
	notes: string
	/** One row per link, blanks included — the modal edits the rows with `@lib/links`. */
	links: string[]
}

export const groupDraftFromFlightGroup = (group: GetFlightGroupResponse): FlightGroupDraft => ({
	notes: group.notes ?? '',
	links: group.links ?? [],
})

/**
 * Notes and links only — the leg membership fields are sent empty so the PATCH
 * leaves the group's legs alone. Emptied notes CLEAR with null.
 */
export const toGroupUpdatePayload = (draft: FlightGroupDraft): PatchFlightGroupType => ({
	addFlightIds: [],
	removeFlightIds: [],
	notes: draft.notes.trim() ? draft.notes.trim() : null,
	links: compactLinks(draft.links),
})
